/**
 * Правила окна «Отправляем заявку в банки».
 *
 * Вынесено из компонента отдельно, чтобы правила можно было проверить тестами:
 * оба они уже один раз ошибались на бою.
 */

/**
 * Что показать по конкретному банку. Бэкенд помечает ответ банка в json заявки
 * (submit_state) сразу, как банк ответил, — это основной источник. Остальное
 * оставлено запасным вариантом: заявки, поданные до этой правки, поля не имеют.
 */
export const resolveAppealState = (appeal) => {
  let submitState = null;
  try {
    submitState = JSON.parse(appeal?.json || '{}')?.submit_state || null;
  } catch (e) {
    submitState = null;
  }
  if (submitState === 'accepted' || submitState === 'failed') return submitState;
  if (appeal?.bankOwnerId) return 'accepted';
  if (appeal?.errorMessage) return 'failed';
  return 'pending';
};

/**
 * Открывать ли окно прогресса.
 *
 * Ни одной заявки — окно не показываем. На первом клике «Сохранить и
 * продолжить» заявки в банки не уходят: бэкенд отвечает «ОТП не подписан», и
 * менеджер идёт отправлять клиенту согласие. Окно в этот момент писало
 * «заявки уже ушли в банки» и вводило в заблуждение.
 */
export const shouldShowSendProgress = ({ loading, banks, error } = {}) => {
  if (error) return true;
  return !!loading && (banks?.length || 0) > 0;
};

/**
 * Можно ли стирать черновик анкеты. Только после реальной отправки: раньше
 * черновик стирался на любом исходе, в том числе на первом клике, и менеджер,
 * обновив страницу, пока клиент подписывает согласие, получал пустую анкету.
 */
export const shouldClearDraft = (submitResult) => submitResult?.sent === true;

/**
 * Суть ошибки банка без технического хвоста.
 *
 * В карточке сделки к тексту ошибки подклеивался стек исключения после метки
 * «--- TRACE ---»: у Форте это доходило до 2500 символов, и суть тонула. Стек
 * остаётся в банковском логе, менеджеру он не нужен. Старые заявки хвост уже
 * содержат, поэтому режем и при отображении, а не только при записи.
 */
const MESSAGE_FIELDS = ['message', 'errorMessage', 'error_description', 'description', 'error', 'detail'];

// Достаёт человеческую строку из JSON-ответа банка. ТФС присылает русский текст
// в \u-экранировании — в карточке он читался как набор символов.
const messageFromJson = (text) => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return null;
  }

  for (const field of MESSAGE_FIELDS) {
    const value = parsed?.[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object') {
      const nested = messageFromJson(JSON.stringify(value));
      if (nested) return nested;
    }
  }
  return null;
};

export const bankErrorSummary = (errorMessage) => {
  if (!errorMessage) return errorMessage;
  const withoutTrace = String(errorMessage).split('--- TRACE ---')[0].trim();
  return messageFromJson(withoutTrace) || withoutTrace;
};

/**
 * Показывать ли информационный статус банка («Ожидает рассмотрения» у Шинхана).
 *
 * Он пишется в заявку в момент подачи и потом не обновляется. Когда банк
 * присылает решение, статус в карточке меняется на «отказано», а эта строка
 * оставалась прежней — на сделке 189321 Шинхан отказал, а под карточкой всё
 * ещё висело «Ожидает рассмотрения». Показываем только пока решения нет.
 */
export const shouldShowBankStatus = (appeal) => {
  if (appeal?.errorMessage) return false;
  return appeal?.statusId === 1;
};
